/**
 * Fin de partie : podium anime, particules or, bouton REJOUER avec le compteur
 * de joueurs restes. La galerie complete des retouches arrivera ici.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { isElimination, type RoomSnapshot } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { connection } from '../net/connection.js';
import { leaveRoom } from '../net/leaveRoom.js';
import { useRoomStore } from '../store/roomStore.js';
import { Avatar } from '../ui/Avatar.js';
import { BotTag } from '../ui/BotTag.js';
import { Screen } from '../ui/Screen.js';
import { navigate } from '../router.js';
import { Gallery } from '../gallery/Gallery.js';
import { showInterstitial } from '../ads/adapter.js';

/** Plafond dur : au-dela, le cout de rendu se voit sur mobile. */
const MAX_PARTICLES = 60;

export function GameEndScreen({ room }: { room: RoomSnapshot }): ReactElement {
  const t = useT();
  const playerId = useRoomStore((state) => state.playerId);

  /**
   * Battle royale : le classement vient de la SURVIE, pas des points.
   *
   * Ce mode n'en compte aucun, un tri au score y mettrait donc tout le monde a
   * egalite a zero et le podium serait un tirage au sort. `rank` porte la place
   * finale, 1 pour le dernier debout.
   */
  const elimination = isElimination(room.settings.mode);

  // Le streamer est hors concours : il ne dessine pas et ne marque aucun point.
  // Le laisser dans le classement le placerait dernier a chaque partie, et
  // pire, il gagnerait le jour ou tout le monde finit a zero.
  const ranked = [...room.players]
    .filter((player) => !player.streamer)
    .sort((a, b) =>
      elimination
        ? (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER)
        : b.score - a.score,
    );
  const podium = ranked.slice(0, 3);
  const rest = ranked.slice(3);
  const winner = podium[0];
  const hasVoted = room.playAgain.includes(playerId ?? '');

  const quit = (): void => {
    leaveRoom();
    navigate('/');
  };

  // Ordre visuel du podium : 2e, 1er, 3e.
  const order = [podium[1], podium[0], podium[2]];
  const heights = [92, 132, 68];

  return (
    <Screen name="end" scroll>
      <main className="end">
        <Particles />

        <header className="end__header">
          <p className="section-label">{t('end.title')}</p>
          {winner !== undefined && (
            <h1 className="end__winner">{t('end.winner', { name: winner.nickname })}</h1>
          )}
        </header>

        <div className="podium">
          {order.map((player, slot) =>
            player === undefined ? (
              <div className="podium__column" key={`empty-${slot}`} />
            ) : (
              <div className="podium__column" key={player.id}>
                {slot === 1 && (
                  <svg className="podium__crown" viewBox="0 0 40 28" aria-hidden="true">
                    <path d="M2 26 L6 6 L14 16 L20 2 L26 16 L34 6 L38 26 Z" fill="var(--gold)" />
                  </svg>
                )}
                <Avatar playerId={player.id} avatar={player.avatar} colorIndex={player.colorIndex} size={slot === 1 ? 64 : 48} />
                <span className="podium__name">
                  {player.nickname}
                  {player.bot && <BotTag />}
                </span>
                {/* Aucun point en battle royale : la place est deja peinte sur
                    la marche, un zero de plus n'apprendrait rien. */}
                {!elimination && (
                  <span className="podium__score mono-tabular">{player.score}</span>
                )}
                <div
                  className="podium__base"
                  style={{ height: heights[slot], animationDelay: `${slot * 90}ms` }}
                >
                  <span className="podium__rank mono-tabular">
                    {slot === 1 ? 1 : slot === 0 ? 2 : 3}
                  </span>
                </div>
              </div>
            ),
          )}
        </div>

        {rest.length > 0 && (
          <ol className="end__list">
            {rest.map((player, index) => (
              <li key={player.id}>
                <span className="mono-tabular">{index + 4}</span>
                <Avatar playerId={player.id} avatar={player.avatar} colorIndex={player.colorIndex} size={22} />
                <span>
                  {player.nickname}
                  {player.bot && <BotTag size={12} />}
                </span>
                {!elimination && <span className="mono-tabular">{player.score}</span>}
              </li>
            ))}
          </ol>
        )}

        <Gallery room={room} />

        <footer className="end__footer">
          <button
            type="button"
            className="button-primary button-block"
            disabled={hasVoted}
            onClick={() => void showInterstitial().finally(() => connection.send({ type: 'playAgain' }))}
          >
            {/* Le total compte les joueurs CONNECTES : quelqu'un qui a quitte
                ne doit pas bloquer un compteur qui n'atteindrait jamais son
                maximum. C'est aussi ce que le serveur attend pour relancer. */}
            {room.playAgain.length > 0
              ? t('end.playAgainCount', {
                  n: room.playAgain.length,
                  total: room.players.filter((player) => player.connected).length,
                })
              : t('end.playAgain')}
          </button>

          {/* Partie rapide : la table se relance seule une fois le premier
              volontaire declare. Le compte a rebours est ANNONCE, une relance
              qui tombe sans prevenir se lit comme un bug. `endsAt` ne porte
              cette echeance qu'en partie rapide : ailleurs on attend le clic
              de chacun, et rien ne s'affiche. */}
          {room.endsAt !== null && <RestartCountdown endsAt={room.endsAt} />}
          <button type="button" className="button-ghost button-block" onClick={quit}>
            {t('end.quit')}
          </button>
        </footer>
      </main>
    </Screen>
  );
}

/**
 * Secondes restantes avant la relance automatique.
 *
 * Compteur local rafraichi a la seconde, hors du store : l'echeance vient du
 * serveur, seul l'affichage est local.
 */
function RestartCountdown({ endsAt }: { endsAt: number }): ReactElement {
  const t = useT();
  const [left, setLeft] = useState(() => Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));

  useEffect(() => {
    const tick = (): void => setLeft(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [endsAt]);

  return <p className="end__restart mono-tabular">{t('end.restartIn', { n: left })}</p>;
}

/** Particules or, en transform/opacity uniquement, une seule passe. */
function Particles(): ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = ref.current;
    if (host === null) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const nodes: HTMLElement[] = [];
    for (let i = 0; i < MAX_PARTICLES; i += 1) {
      const particle = document.createElement('span');
      particle.className = 'particle';
      particle.style.left = `${Math.random() * 100}%`;
      particle.style.animationDelay = `${Math.random() * 1200}ms`;
      particle.style.animationDuration = `${2200 + Math.random() * 1600}ms`;
      particle.style.transform = `scale(${0.5 + Math.random()})`;
      host.appendChild(particle);
      nodes.push(particle);
    }
    return () => {
      for (const node of nodes) node.remove();
    };
  }, []);

  return <div className="particles" ref={ref} aria-hidden="true" />;
}
